/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */

// OLD COMMENTS BELOW
/*
Cloud filtering/masking block

LANDSAT algorithms:
  - filter by cloud cover + median  TOA -> TOA (default?)
  - simpleComposite() cloud score   RAW -> TOA: Algorithm converts scenes to TOA reflectance, applies simple cloud score and takes the median of the least cloudy pixels
  - min()               TOA -> TOA
  - cloud masking using cloud score TOA -> TOA . Scores the images based upon their cloudiness, masks the pixels and then creates a median
  - SR bitmasks           SR  -> TOA (issue: SR conversion to TOA ?)
  - BAP

SENTINEL-2 Algorithms
  - Bitwise masks + median   (default)
  - min()
  - QA cloud mask
  - S2 clouscore mask
  - QA + S2 cloudscore mask
*/

import * as Blockly from 'blockly/core';
import 'blockly/python';
import { getAllowedOutputs } from '../block_compatibility_checks';
import { hasSatChanged, updateBlockSat } from '../helper_functions';

const cloudFilterCompatibilityList = {
    'landsat_TOA': [
        ['Simple cloud score (default)', 'simple_cloud_score'],
        // ['Cloud cover filter and median', "filter_median"], 
        // ['Simple composite', 'simple_composite'], 
        // ['Minimum', 'landsat_min'], 
    ],
    'landsat_SR': [
        ['Simple cloud score + mask (default)', 'L8_SR_Cloud_Mask']
    ],
    'sentinel_TOA': [
        ['QA+S2 cloudscore (default)', 'qa_s2']
    ],
    'sentinel_SR': [
        ['QA+S2 cloudscore (default)', 'qa_s2_SR'],
        ['S2cloudless', 's2cloud']
    ],
    'else': [
        ['Cloud cover filter and median', "filter_median"],
        ['Simple composite', 'simple_composite'],
        ['Minimum', 'else_min'],
        ['Simple cloud score DEFAULT LANDSAT TOA', 'simple_cloud_score'],
        ['Cloud and cirrus masking + median', 'bitwise_median'],
        ['QA+S2 cloudscore DEFAULT SENTINEL TOA', 'qa_s2'],
        ['QA+S2 cloudscore DEFAULT SENTINEL SR', 'qa_s2_SR'],
        ['Simple cloud score + mask DEFAULT LANDSAT SR', 'L8_SR_Cloud_Mask'],
        ['S2cloudless', 's2cloud']
    ]
}

/**
 * object containing keys that line up with the dropdown and add parameters to a given
 * dummy input
 */
const cloudFilterParamList = {
    'filter_median': (dummyInput) => {
        dummyInput
            .appendField("Cloud cover percentage:    ")
            .appendField(new Blockly.FieldNumber(40, 0, 100, 1), 'filter_median_cloud_cover_percentage')
    },
    'simple_composite': (dummyInput) => {
        dummyInput
            .appendField("Percentile:   ")
            .appendField(new Blockly.FieldNumber(90, 0, 100, 1), "simple_composite_percentile")
            .appendField("  Cloud score range: ")
            .appendField(new Blockly.FieldNumber(10), "simple_composite_cloud_score")
            .appendField("  Max depth: ",)
            .appendField(new Blockly.FieldNumber(40), "simple_composite_max_depth")
    },
    'simple_cloud_score': (dummyInput) => {
        dummyInput
            .appendField("Cloud score:  ")
            .appendField(new Blockly.FieldNumber(20), "simple_cloud_score")
    },
    'qa_s2': (dummyInput) => {
        dummyInput
            .appendField("Cloud threshold: ",)
            .appendField(new Blockly.FieldNumber(1), "qa_s2_cloud_threshold")
            .appendField(" Blue threshold: ")
            .appendField(new Blockly.FieldNumber(0.1), "qa_s2_blue_threshold_min")
            .appendField(" - ")
            .appendField(new Blockly.FieldNumber(0.4), "qa_s2_blue_threshold_max")
            .appendField(" Aerosol threshold: ")
            .appendField(new Blockly.FieldNumber(0.1), "qa_s2_aerosol_threshold_min")
            .appendField(" - ")
            .appendField(new Blockly.FieldNumber(0.45), "qa_s2_aerosol_threshold_max")
    },
    'L8_SR_Cloud_Mask': (dummyInput) => {
        dummyInput
            .appendField("Cloud score:  ")
            .appendField(new Blockly.FieldNumber(20), "L8_SR_Cloud_Mask_cloud_cover")
    },
    'qa_s2_SR': (dummyInput) => {
        dummyInput
            .appendField("Max Cloud Cover:  ")
            .appendField(new Blockly.FieldNumber(20, 0, 100), "qa_s2_SR_cloud_cover")
    }, 
    's2cloud': (dummyInput) => {
        dummyInput
            .appendField("Cloud score:  ")
            .appendField(new Blockly.FieldNumber(60), "S2cloudless_cloud_cover")   
    }
}

Blockly.Blocks['analyse_cloud_filter'] = {
    init: function () {
        // generates cloud filter list, must be called within here to avoid weird issues with the way blockly updates fields
        // appears to remove the field when you update the options

        this.appendDummyInput('cloudFilterFirstLine')
            .appendField("Cloud filtering algorithm:   ", 'alg')
            .appendField(new Blockly.FieldDropdown(
                this.generateCloudFilterListFromSat('null'), // generates list of dropdown options ON USER CLICK WHY WHY WHY BLOCKLY
                this.validateFilterDropdown // validates when a user changes their selection
            ), 'filterType')

        this.setPreviousStatement(true, 'optical_filter');
        this.setNextStatement(true, getAllowedOutputs('optical_filter'));
        this.setInputsInline(false);
        this.setTooltip(Blockly.Msg.CLOUD_FILTER_BLOCK_TOOLTIP);
        this.setHelpUrl("");
        this.setStyle('cloudFilter');
    },

    /**
     * for a given satellite return a list of valid blockly dropdown options containing valid cloud filters
     * attempts to reset the selection when the user changes satellite, however doesn't occur until the user clicks on the 
     * dropdown after they've made the sat change, attempt above to use an on change function but this caused bugs
     * @param {*} sat 
     * @returns 
     */
    generateCloudFilterListFromSat: function (sat) {
        let filterList = cloudFilterCompatibilityList['else'];

        if (sat == 'l8' || sat == 'l7' || sat == 'l5' || sat == 'l4') {
            filterList = cloudFilterCompatibilityList['landsat_TOA'];
        } else if (sat == 's2') {
            filterList = cloudFilterCompatibilityList['sentinel_TOA'];
        } else if (sat == 'l8_SR' || sat == 'l7_SR' || sat == 'l5_SR' || sat == 'l4_SR') {
            filterList = cloudFilterCompatibilityList['landsat_SR']
        } else if (sat == 's2_SR') {
            filterList = cloudFilterCompatibilityList['sentinel_SR']
        }

        return filterList;
    },

    /**
     * on filter change, update parameters on block
     * @param {*} newValue - filter value
     */
    validateFilterDropdown: function (newValue) {
        // second param is true for quiet errors, i.e. when the input doesn't exist
        // as this doesn't matter
        let sourceBlock = this.getSourceBlock();
        sourceBlock.removeInput("filterParams", true);

        // check if filterValue is in the param list i.e. check if new params are needed
        if (newValue in cloudFilterParamList) {
            // adds dummy input, calls function to add params
            let dummyInput = sourceBlock.appendDummyInput("filterParams");
            cloudFilterParamList[newValue](dummyInput);
        }

        return newValue;
    },

    /**
     * removes the current inputs on the blocks, adds in new ones with correct list of available algorithms
     * then forces a call to update the parameters below it
     */
    updateAlgorithmDropdown: function () {
        this.removeInput("cloudFilterFirstLine") // remove dropdown line
        this.removeInput("filterParams", true) // remove params line

        let cloudFilterList = this.generateCloudFilterListFromSat(this.sat)

        this.appendDummyInput('cloudFilterFirstLine')
            .appendField("Cloud filtering algorithm:   ", 'alg')
            .appendField(new Blockly.FieldDropdown(
                cloudFilterList, // generates list of dropdown options ON USER CLICK WHY WHY WHY BLOCKLY
                this.validateFilterDropdown // validates when a user changes their selection
            ), 'filterType')

        // force update on field, means validate function gets called and params are added
        this.setFieldValue(cloudFilterList[0][1], "filterType")
    },

    /**
     * https://developers.google.com/blockly/reference/js/Blockly.Block#onchange
     */
    onchange: function (event) {
        // adding checks for the end of a drag event i.e. a block has been moved somewhere
        // e.g. outside of its parent block, change events pick up whenever something
        // in the entire block hierarchy has changed e.g. the user changes a dropdown in the parent
        // block
        // DO NOT ALLOW event.type == 'move' THIS WILL CAUSE HEADACHES
        // event.type == 'create' is also quite janky
        // using finished_loading event to capture when user loads workflow or when a featuredWorkflow is clicked
        if((event.type == 'drag' && !event.isStart) || event.type == 'change' || event.type == 'finished_loading'){
            updateBlockSat(this);
            if (hasSatChanged(this)) {
                this.updateAlgorithmDropdown();
            }
        }
    },
};